/* eslint-disable react/no-array-index-key */
import {getMedicationProfileStabilityRules} from '@/modules/drug-catalog/medication-profiles/functions'
import {MedicationProfileFormSchema} from '@/modules/drug-catalog/medication-profiles/schemas'
import {useStabilityRuleStorageLabel} from '@/modules/drug-catalog/stability-rules/functions'
import {StabilityRuleFormSchema} from '@/modules/drug-catalog/stability-rules/schemas'
import {getFirstElementOrSelf} from '@cypro/ui/utils'
import {Badge, BadgeProps, Box, Heading, HStack, IconButton, Menu, Separator, Text} from '@chakra-ui/react'
import {MedicationProfile, StabilityRule} from '@cypro/engine/schema'
import {useTranslate} from '@tolgee/react'
import {useMemo} from 'react'
import {LuEllipsisVertical, LuPen, LuTrash2} from 'react-icons/lu'

interface Props {
  profile: MedicationProfileFormSchema | MedicationProfile
  onDelete?: VoidFunction
  onEdit?: VoidFunction
}

export function MedicationProfileCard({profile, onDelete, onEdit}: Props) {
  const {t} = useTranslate('common')

  const badges = useMemo(() => {
    const value: BadgeProps[] = []

    if (profile.reconstitutionRequired) value.push({children: t('reconstitution_required'), colorPalette: 'orange'})

    return value
  }, [t, profile])

  const stabilityRules = getMedicationProfileStabilityRules(profile)

  let name = profile.name
  if (!name && 'medicineForm' in profile) {
    name = profile.medicineForm?.name || ''
  }
  if (!name && 'medicineFormName' in profile) {
    name = profile.medicineFormName || ''
  }

  return (
    <Box
      padding="5"
      spaceY="4"
      borderWidth="1px"
      borderStyle="solid"
      borderColor="border"
      borderRadius="l3"
      background="bg"
    >
      <Box spaceY="2">
        <HStack width="100%" spaceX="2" justifyContent="space-between">
          {name && (
            <Heading as="h3" fontSize="sm">
              {name}
            </Heading>
          )}

          {(onEdit || onDelete) && (
            <Menu.Root>
              <Menu.Trigger asChild>
                <IconButton size="sm" variant="ghost" title={t('more_actions')}>
                  <LuEllipsisVertical />
                </IconButton>
              </Menu.Trigger>
              <Menu.Positioner>
                <Menu.Content>
                  {onEdit && (
                    <Menu.Item value="edit" onSelect={onEdit}>
                      <LuPen />
                      {t('edit')}
                    </Menu.Item>
                  )}
                  {onDelete && (
                    <Menu.Item value="delete" onSelect={onDelete}>
                      <LuTrash2 />
                      {t('delete')}
                    </Menu.Item>
                  )}
                </Menu.Content>
              </Menu.Positioner>
            </Menu.Root>
          )}
        </HStack>

        <HStack width="100%" flexWrap="wrap">
          {badges.map((badge, index) => (
            <Badge key={index} size="sm" {...badge}>
              {badge.children}
            </Badge>
          ))}
        </HStack>
      </Box>

      {Boolean(stabilityRules.length) && (
        <>
          <Separator marginTop="4" marginBottom="2" />

          <Box spaceY="2">
            {stabilityRules.map((rule, index) => {
              if (!rule) return null
              return <StabilityRuleRow key={index} {...rule} />
            })}
          </Box>
        </>
      )}
    </Box>
  )
}

function StabilityRuleRow(rule: StabilityRule | StabilityRuleFormSchema) {
  const {t} = useTranslate('common')
  const stabilityStage = getFirstElementOrSelf(rule.stabilityStage)
  const durationUnit = getFirstElementOrSelf(rule.durationUnit)
  const storageLabel = useStabilityRuleStorageLabel(rule)

  return (
    <Box>
      <Text fontSize="xs">
        {stabilityStage && `${t(stabilityStage)}: `}
        {rule.durationValue} {t(durationUnit)}
        {' @ '}
        {storageLabel}
      </Text>
    </Box>
  )
}
